import React, { useContext } from "react";
import { useQuery } from "@tanstack/react-query";
import { useParams, Link } from "react-router-dom";
import {
  ArrowLeft,
  Users,
  BookOpen,
  Calendar,
  GraduationCap,
  Eye,
} from "lucide-react";
import LoadingSpinner from "../../components/common/LoadingSpinner";
import { AuthContext } from "../../contexts/AuthContext";
import { getMyTeacherProfile } from "../../services/teachers";
import { getStudents } from "../../services/students";

const TeacherClassDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const authContext = useContext(AuthContext);
  const user = authContext?.user;

  // Fetch teacher profile to get class details
  const { data: teacherData, isLoading: teacherLoading } = useQuery({
    queryKey: ["teacher-profile"],
    queryFn: () => getMyTeacherProfile(),
    enabled: !!user?.id,
  });

  // Fetch students to get class students
  const { data: studentsData, isLoading: studentsLoading } = useQuery({
    queryKey: ["students"],
    queryFn: () => getStudents(),
    enabled: !!user?.id,
  });

  if (teacherLoading || studentsLoading) {
    return <LoadingSpinner />;
  }

  const teacher = teacherData?.data;
  const allStudents = studentsData?.data || [];

  // Find the specific class assignment
  const classAssignment = teacher?.currentClasses?.find(
    (cls: any) => cls.classId._id === id
  );

  if (!classAssignment) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <h3 className="text-red-800 font-medium">Class Not Found</h3>
          <p className="text-red-600 mt-1">
            You don't have access to this class or it doesn't exist.
          </p>
          <Link
            to="/teacher/classes"
            className="inline-flex items-center mt-3 text-red-600 hover:text-red-800"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to Classes
          </Link>
        </div>
      </div>
    );
  }

  const classInfo = classAssignment.classId;

  // Filter students for this class
  const classStudents = allStudents.filter(
    (student: any) => student.classId === classInfo._id
  );

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <Link
          to="/teacher/classes"
          className="inline-flex items-center text-gray-600 hover:text-gray-800 mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to Classes
        </Link>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              {classInfo.name}
            </h1>
            <p className="text-gray-600 mt-1">{classInfo.fullName}</p>
            {classAssignment.isClassTeacher && (
              <span className="inline-block bg-blue-100 text-blue-800 text-sm px-3 py-1 rounded-full mt-2">
                Class Teacher
              </span>
            )}
          </div>
          <div className="flex space-x-3">
            <Link
              to={`/teacher/attendance?classId=${classInfo._id}`}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              <Calendar className="h-4 w-4 mr-2" />
              View Attendance
            </Link>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Class Information */}
        <div className="lg:col-span-2 space-y-6">
          {/* Basic Info */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              Class Information
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center text-gray-600">
                <GraduationCap className="h-5 w-5 mr-3" />
                <div>
                  <span className="text-sm text-gray-500">Grade:</span>
                  <p className="font-medium">
                    {classInfo.grade || "Not specified"}
                  </p>
                </div>
              </div>
              <div className="flex items-center text-gray-600">
                <Users className="h-5 w-5 mr-3" />
                <div>
                  <span className="text-sm text-gray-500">Capacity:</span>
                  <p className="font-medium">
                    {classInfo.currentEnrollment || 0} /{" "}
                    {classInfo.capacity || 0} students
                  </p>
                </div>
              </div>
              <div className="flex items-center text-gray-600">
                <Calendar className="h-5 w-5 mr-3" />
                <div>
                  <span className="text-sm text-gray-500">Started:</span>
                  <p className="font-medium">
                    {classAssignment.startDate
                      ? new Date(classAssignment.startDate).toLocaleDateString()
                      : "Not specified"}
                  </p>
                </div>
              </div>
              <div className="flex items-center text-gray-600">
                <BookOpen className="h-5 w-5 mr-3" />
                <div>
                  <span className="text-sm text-gray-500">Courses:</span>
                  <p className="font-medium">
                    {classAssignment.courses?.length || 0} assigned
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Assigned Courses */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              Assigned Courses
            </h2>
            {classAssignment.courses && classAssignment.courses.length > 0 ? (
              <div className="space-y-3">
                {classAssignment.courses.map((course: any) => (
                  <div
                    key={course._id}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                  >
                    <div>
                      <h3 className="font-medium text-gray-900">
                        {course.name}
                      </h3>
                      <p className="text-sm text-gray-600">
                        {course.category} • {course.credits} credits •{" "}
                        {course.level}
                      </p>
                    </div>
                    <Link
                      to={`/teacher/courses/${course._id}`}
                      className="flex items-center text-blue-600 hover:text-blue-800"
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      View
                    </Link>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500">
                No courses assigned to this class yet.
              </p>
            )}
          </div>

          {/* Class Students */}
          {/* <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900">
                Students ({classStudents.length})
              </h2>
              <Link
                to={`/teacher/students?classId=${classInfo._id}`}
                className="text-blue-600 hover:text-blue-800 text-sm font-medium"
              >
                View All Students
              </Link>
            </div>
            {classStudents.length > 0 ? (
              <div className="space-y-3">
                {classStudents.slice(0, 5).map((student: any) => (
                  <div
                    key={student._id}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                  >
                    <div className="flex items-center">
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                        <span className="text-blue-600 font-medium text-sm">
                          {(student.userInfo?.firstName ||
                            student.firstName ||
                            "U")[0].toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <h3 className="font-medium text-gray-900">
                          {student.userInfo?.firstName || student.firstName}{" "}
                          {student.userInfo?.lastName || student.lastName}
                        </h3>
                        <p className="text-sm text-gray-600">
                          {student.studentId || student.rollNumber}
                        </p>
                      </div>
                    </div>
                    <span
                      className={`px-2 py-1 text-xs rounded-full ${
                        student.status === "active"
                          ? "bg-green-100 text-green-800"
                          : "bg-gray-100 text-gray-800"
                      }`}
                    >
                      {student.status || "Active"}
                    </span>
                  </div>
                ))}
                {classStudents.length > 5 && (
                  <p className="text-center text-gray-500 text-sm pt-2">
                    And {classStudents.length - 5} more students...
                  </p>
                )}
              </div>
            ) : (
              <p className="text-gray-500">
                No students enrolled in this class yet.
              </p>
            )}
          </div> */}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Quick Actions */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Quick Actions
            </h3>
            <div className="space-y-3">
              <Link
                to={`/teacher/attendance?classId=${classInfo._id}`}
                className="w-full flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                <Calendar className="h-4 w-4 mr-2" />
                View Attendance
              </Link>
              <Link
                to={`/teacher/students?classId=${classInfo._id}`}
                className="w-full flex items-center justify-center px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
              >
                <Users className="h-4 w-4 mr-2" />
                View Students
              </Link>
              <Link
                to={`/teacher/dashboard`}
                className="w-full flex items-center justify-center px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
              >
                <GraduationCap className="h-4 w-4 mr-2" />
                Assessments
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeacherClassDetail;
